import React, { useCallback, useEffect } from 'react';

import { AnyAri } from '@atlassian/ari';
import { usePreviewPanelsActions } from '@atlassian/preview-panels-api';

import { DummyPreviewPanel } from './panels/common/DummyPreviewPanel';
import { JiraIssuePreviewPanel } from './panels/JiraIssuePreviewPanel';
import type { GlobalObjectType } from './types';
import { isInIframe } from './utils';

interface GlobalObjectPanelsProps {
	supportedObjects: GlobalObjectType[];
}

interface ReturnType {
	isGlancePanelAvailable: (props: { ari: string }) => boolean;
	openGlancePanel: (props: { url: string; ari: string; name?: string; iconUrl?: string }) => void;
}

const getGlobalObjectType = (ari: string): GlobalObjectType => {
	const parsedAri = AnyAri.parse(ari);
	return `${parsedAri.resourceOwner}-${parsedAri.resourceType}` as GlobalObjectType;
};

export const useGlobalObjectPreviewPanels = ({
	supportedObjects,
}: GlobalObjectPanelsProps): ReturnType => {
	const { open } = usePreviewPanelsActions();

	const isGlancePanelAvailable = useCallback(
		({ ari }: { ari: string }): boolean => supportedObjects.includes(getGlobalObjectType(ari)),
		[supportedObjects],
	);

	const openGlancePanel = useCallback(
		({
			url,
			ari,
			name,
			iconUrl,
		}: {
			url: string;
			ari: string;
			name?: string;
			iconUrl?: string;
		}): void => {
			const globalObjectType = getGlobalObjectType(ari);

			if (!name) {
				throw new Error(`Name is required to open a glance panel for ${globalObjectType}`);
			}

			// Check if we're in an iframe (not the top-most frame)
			if (isInIframe()) {
				// Post message to parent frame to open the panel
				window.parent.postMessage(
					{
						type: 'OPEN_GLANCE_PANEL',
						payload: {
							url,
							ari,
							name,
							iconUrl,
						},
					},
					'*',
				);
				return;
			}

			let panelContent;
			switch (globalObjectType) {
				case 'jira-issue':
					panelContent = <JiraIssuePreviewPanel name={name} url={url} iconUrl={iconUrl} />;
					break;
				case 'identity-team':
				case 'confluence-page':
				case 'townsquare-goal':
				case 'townsquare-project':
					panelContent = <DummyPreviewPanel name={name} url={url} />;
					break;

				default:
					throw new Error(`Unsupported global object type: ${globalObjectType}`);
			}

			open({
				panel: {
					id: ari,
					type: globalObjectType,
					content: () => panelContent,
				},
			});
		},
		[open],
	);

	// Listen for messages from iframes to open panels
	useEffect(() => {
		const handleMessage = (event: MessageEvent) => {
			// Only handle messages with the expected type
			if (event.data?.type !== 'OPEN_GLANCE_PANEL') {
				return;
			}

			const { payload } = event.data;
			if (payload?.url && payload?.ari && payload?.name) {
				openGlancePanel({
					url: payload.url,
					ari: payload.ari,
					name: payload.name,
					iconUrl: payload.iconUrl,
				});
			}
		};

		window.addEventListener('message', handleMessage);

		return () => {
			window.removeEventListener('message', handleMessage);
		};
	}, [openGlancePanel]);

	return {
		isGlancePanelAvailable,
		openGlancePanel,
	};
};
